import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import ExpansionDetails from "../ExpansionDetails/ExpansionDetails";
import EventDetails from "../EventDetails/EventDetails";
import { numberFormat } from "../../extends/NumberFormat";
import { compose } from "recompose";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
  CircularProgress,
  Grid,
} from "@mui/material/";

import withStyles from "@mui/styles/withStyles";

// icons
import eventIcon from "../../img/dropdown-icons/EventDetails.svg";
import beverageIcon from "../../img/dropdown-icons/Beverage.svg";
import financialsIcon from "../../img/dropdown-icons/EventFinancials.svg";
import menusIcon from "../../img/dropdown-icons/Menus.svg";
import miscellaneousIcon from "../../img/dropdown-icons/Miscellaneous.svg";
import rentalIcon from "../../img/dropdown-icons/Rental.svg";
import staffingIcon from "../../img/dropdown-icons/Staffing.svg";

import config from "../../config/configAPI";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Staff from "../EventStaff/Staff";
import EventMenu from "../EventMenu/EventMenu";
import EventRental from "../EventRental/EventRental";
import EventFinancials from "../EventFinancials/EventFinancials";
import CommentBox from "../CommentBox/CommentBox";
import ModalMessage from "../ModalMessage/ModalMessage";
import { httpGet, httpPost } from "../../hooks/http";
import { UserContext } from "../../UserContext";

const withWidth = () => (WrappedComponent) => (props) =>
  <WrappedComponent {...props} width="xs" />;

const styles = (theme) => ({
  centerSpiner: {
    margin: "auto",
    textAlign: "center",
  },
  font: {
    color: "black",
    fontWeight: "bold",
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
    flexBasis: "50%",
    fontWeight: "bold",
    flexShrink: 0,
  },
  margin: {
    marginBottom: 24,
  },
  icon: {
    marginRight: 10,
  },
  secondaryHeading: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary,
    marginLeft: "auto",
  },
  space: {
    padding: 10,
  },
  padding: {
    padding: 13,
  },
});

const checkToDisplay = (result) => {
  return Object.keys(result.sectionComments).map((element) => {
    switch (element) {
      case "menu":
        result.hasMenus = result.sectionComments[element].shown;
        break;
      case "miscellaneous":
        result.hasMiscellaneous = result.sectionComments[element].shown;
        break;
      case "financial":
        result.hasFinancial = result.sectionComments[element].shown;
        break;
      case "overallComments":
        result.hasOverallComments = result.sectionComments[element].shown;
        break;
      case "info":
        result.hasInfo = result.sectionComments[element].shown;
        break;
      case "beverage":
        result.hasBeverages = result.sectionComments[element].shown;
        break;
      case "rentals":
        result.hasRentals = result.sectionComments[element].shown;
        result.hasOtherInventory = result.sectionComments[element].shown;
        break;
      case "staff":
        result.hasStaff = result.sectionComments[element].shown;
        break;

      default:
        break;
    }
    return result;
  });
};

const CurrentEvent = (props) => {
  const { settings, access_token, businessInfo, canViewComments } =
    useContext(UserContext);
  const { classes, pastEvent, eventId, match } = props;

  const [menuSections, setMenuSections] = useState({});
  const [comments, setComments] = useState([]);
  const [open, setOpen] = useState(false);
  const [clear, setClear] = useState(false);
  const _eventId = match?.params?.id || eventId;
  const [eventData, setEventData] = useState(null);
  const [overalComment, setOveralComment] = useState("");
  const loaded = menuSections && Object.keys(menuSections).length > 0;

  const submitAllComments = async () => {
    if (comments.length > 0 || overalComment.length > 0) {
      const uniqueComments = comments
        .map((e, i, final) => final.indexOf(e) === i && i)
        .filter((e) => comments[e])
        .map((e) => comments[e]);

      if (overalComment.length > 0) {
        menuSections.eventInfo.newComment.content = overalComment;
        uniqueComments.push(menuSections.eventInfo.newComment);
      }

      await httpPost(
        `events/${_eventId}/clientportalcomments`,
        uniqueComments,
        access_token
      ).then((result) => {
        setOpen(true);
        setOveralComment("");
        setComments([]);
        setClear(true);
        props.detectComment(comments);
      });
    }
  };

  const [submit, setSubmit] = useState(true);
  const setComment = (newComment) => {
    if (newComment.content !== "") {
      setComments((comments) => [...comments, newComment]);

      if (comments.length === 0 && newComment.content !== "") {
        setOpen(true);
        setSubmit(false);
      }
    }
  };

  const handleChange = async (panel) => {
    if (panel !== "comment") {
      if (!menuSections[panel]) {
        httpGet(
          `clientPortal/events/${_eventId}/components${config.LINKS[panel]}`,
          access_token
        ).then((result) => {
          const { response, loaded } = result;
          if (loaded) {
            if (panel === "eventInfo") result = { eventInfo: response };
            setMenuSections({ ...menuSections, ...response });
          }
        });
      }
    }
  };

  const closeModal = () => {
    setOpen(false);
    setSubmit(true);
    setClear(false);
  };

  useEffect(() => {
    if (!eventData) return;
    const { response, loaded } = eventData;
    response.hasComments = true;
    if (loaded) {
      response.hasInfo = true;
      response.hasOverallComments = true;
      response.hasFinancial = true;
      if (canViewComments) {
        let commentCount = Object.values(response.sectionComments).some(
          (element) => element.shown === true
        );
        commentCount
          ? (response.hasComments = true)
          : (response.hasComments = false);
        checkToDisplay(response);
      }
      setMenuSections((prev) => {
        return { ...prev, eventInfo: response };
      });
      Object.keys(settings).map((value, index) => {
        if (settings[value] && typeof settings[value] === "object")
          settings[value].isPriceOverridden = response.event.isPriceOverridden;
        return settings;
      });
    }
  }, [eventData, canViewComments, settings]);

  useEffect(() => {
    httpGet(
      `clientPortal/events/${_eventId}/components/eventInfo`,
      access_token
    ).then((result) => {
      setEventData(result);
    });
  }, [_eventId, access_token]);

  if (!businessInfo.culture || !loaded) {
    return (
      <Grid container justifyContent="center">
        <Grid item>
          <CircularProgress className={classes.centerSpiner} />
        </Grid>
      </Grid>
    );
  }
  if (!menuSections?.eventInfo?.hasComments) {
    return (
      <Grid container justifyContent="center">
        <Grid item>
          <Typography variant="h5">No comments to preview.</Typography>
        </Grid>
      </Grid>
    );
  }
  return (
    <Grid
      container
      direction="row"
      justifyContent="center"
      alignItems="center"
      className={classes.space}
    >
      {menuSections.eventInfo && (
        <ModalMessage
          closeModal={closeModal}
          event={open}
          submit={submit}
          catersName={
            menuSections.eventInfo.event.businessLocation
              ? menuSections.eventInfo.event.businessLocation.name
              : businessInfo.name
          }
        />
      )}

      {!canViewComments && (
        <Grid item xs={12} className={classes.margin}>
          {menuSections.eventInfo && menuSections.eventInfo.event.title ? (
            <Typography className={classes.heading}>
              {`${menuSections.eventInfo.event.primaryContact.lastName} : ${menuSections.eventInfo.event.title}`}
            </Typography>
          ) : null}
        </Grid>
      )}

      <Grid item xs={12} className={classes.margin}>
        {menuSections.eventInfo.hasInfo && (
          <Accordion
            className={classes.margin}
            defaultExpanded={true}
            onChange={() => {
              handleChange("eventInfo");
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <img src={eventIcon} alt={eventIcon} className={classes.icon} />
              <Typography className={classes.heading}>Event Details</Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.padding}>
              {loaded && menuSections.eventInfo ? (
                <EventDetails
                  setComment={setComment}
                  canViewComments={canViewComments}
                  clear={clear}
                  pastEvent={pastEvent}
                  eventState={menuSections.eventInfo}
                />
              ) : (
                <CircularProgress className={classes.centerSpiner} />
              )}
            </AccordionDetails>
          </Accordion>
        )}

        {menuSections.eventInfo.hasMenus && (
          <Accordion
            className={classes.margin}
            onChange={() => {
              handleChange("eventMenus");
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} cursor="pointer">
              <img src={menusIcon} alt={menusIcon} className={classes.icon} />
              <Typography className={classes.heading}>
                {businessInfo.sectionLabels[0].name}
              </Typography>
              <Typography className={classes.secondaryHeading}>
                {menuSections.eventInfo.event.financials.foodTotal !== 0 &&
                settings.menuOptions.showSubtotals
                  ? numberFormat(
                      menuSections.eventInfo.event.financials.foodTotal
                    )
                  : null}
              </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.padding}>
              {loaded && menuSections.eventMenus ? (
                <EventMenu
                  setComment={setComment}
                  clear={clear}
                  canViewComments={canViewComments}
                  pastEvent={pastEvent}
                  settings={settings.menuOptions}
                  menu={menuSections.eventMenus}
                />
              ) : (
                <CircularProgress className={classes.centerSpiner} />
              )}
            </AccordionDetails>
          </Accordion>
        )}

        {menuSections.eventInfo.hasBeverages && (
          <Accordion
            className={classes.margin}
            onChange={() => {
              handleChange("beverages");
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <img
                src={beverageIcon}
                alt={beverageIcon}
                className={classes.icon}
              />

              <Typography className={classes.heading}>
                {businessInfo.sectionLabels[1].name}
              </Typography>
              <Typography className={classes.secondaryHeading}>
                {menuSections.eventInfo.event.financials.beverageTotal !== 0 &&
                settings.beverageOptions.showSubtotals
                  ? numberFormat(
                      menuSections.eventInfo.event.financials.beverageTotal
                    )
                  : null}
              </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.padding}>
              {loaded && menuSections.beverages ? (
                <ExpansionDetails
                  setComment={setComment}
                  clear={clear}
                  type="beverage"
                  canViewComments={canViewComments}
                  pastEvent={pastEvent}
                  settings={settings.beverageOptions}
                  menu={menuSections.beverages}
                />
              ) : (
                <CircularProgress className={classes.centerSpiner} />
              )}
            </AccordionDetails>
          </Accordion>
        )}

        {(menuSections.eventInfo.hasRentals ||
          menuSections.eventInfo.hasOtherInventory) && (
          <Accordion
            className={classes.margin}
            onChange={() => {
              handleChange("rentals");
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <img src={rentalIcon} alt={rentalIcon} className={classes.icon} />

              <Typography className={classes.heading}>
                {businessInfo.sectionLabels[2].name} &{" "}
                {businessInfo.sectionLabels[3].name}
              </Typography>
              <Typography className={classes.secondaryHeading}>
                {menuSections.eventInfo.event.financials.rentalTotal +
                  menuSections.eventInfo.event.financials
                    .otherInventoryTotal !==
                  0 && settings.rentalOptions.showSubtotals
                  ? numberFormat(
                      menuSections.eventInfo.event.financials.rentalTotal +
                        menuSections.eventInfo.event.financials
                          .otherInventoryTotal
                    )
                  : null}
              </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.padding}>
              {loaded && menuSections.rentals ? (
                <EventRental
                  settings={settings.rentalOptions}
                  setComment={setComment}
                  canViewComments={canViewComments}
                  pastEvent={pastEvent}
                  displaySettings={[
                    menuSections.eventInfo.hasRentals,
                    menuSections.eventInfo.hasOtherInventory,
                  ]}
                  clear={clear}
                  menu={[menuSections.rentals, menuSections.otherInventory]}
                />
              ) : (
                <CircularProgress className={classes.centerSpiner} />
              )}
            </AccordionDetails>
          </Accordion>
        )}

        {menuSections.eventInfo.hasMiscellaneous && (
          <Accordion
            className={classes.margin}
            onChange={() => {
              handleChange("miscellaneous");
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <img
                src={miscellaneousIcon}
                alt={miscellaneousIcon}
                className={classes.icon}
              />

              <Typography className={classes.heading}>
                {businessInfo.sectionLabels[4].name}
              </Typography>
              <Typography className={classes.secondaryHeading}>
                {menuSections.eventInfo.event.financials.miscellaneousTotal !==
                  0 && settings.miscOptions.showSubtotals
                  ? numberFormat(
                      menuSections.eventInfo.event.financials.miscellaneousTotal
                    )
                  : null}
              </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.padding}>
              {loaded && menuSections.miscellaneous ? (
                <ExpansionDetails
                  setComment={setComment}
                  type="miscellaneous"
                  settings={settings.miscOptions}
                  pastEvent={pastEvent}
                  clear={clear}
                  canViewComments={canViewComments}
                  menu={menuSections.miscellaneous}
                />
              ) : (
                <CircularProgress className={classes.centerSpiner} />
              )}
            </AccordionDetails>
          </Accordion>
        )}

        {menuSections.eventInfo.hasStaff && (
          <Accordion
            className={classes.margin}
            onChange={() => {
              handleChange("eventStaff");
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <img
                src={staffingIcon}
                alt={staffingIcon}
                className={classes.icon}
              />

              <Typography className={classes.heading}>
                {businessInfo.sectionLabels[6].name}
              </Typography>
              <Typography className={classes.secondaryHeading}>
                {menuSections.eventInfo.event.financials.staffTotal !== 0 &&
                settings.staffOptions.showSubtotals
                  ? numberFormat(
                      menuSections.eventInfo.event.financials.staffTotal
                    )
                  : null}
              </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.padding}>
              {loaded && menuSections.eventStaff ? (
                <Staff
                  setComment={setComment}
                  settings={settings.staffOptions}
                  pastEvent={pastEvent}
                  clear={clear}
                  canViewComments={canViewComments}
                  currency={businessInfo.culture.currencyFormat.symbol}
                  staff={menuSections.eventStaff}
                />
              ) : (
                <CircularProgress className={classes.centerSpiner} />
              )}
            </AccordionDetails>
          </Accordion>
        )}

        {menuSections.eventInfo.hasFinancial && (
          <Accordion
            className={classes.margin}
            onChange={() => {
              handleChange("financials");
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <img
                src={financialsIcon}
                alt={financialsIcon}
                className={classes.icon}
              />

              <Typography className={classes.heading}>
                Event Financials
              </Typography>
              <Typography className={classes.secondaryHeading}>
                {menuSections.eventInfo.event.financials.total !== 0
                  ? numberFormat(menuSections.eventInfo.event.financials.total)
                  : null}
              </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.padding}>
              {loaded && menuSections.financials ? (
                <EventFinancials
                  setComment={setComment}
                  clear={clear}
                  pastEvent={pastEvent}
                  canViewComments={canViewComments}
                  event={menuSections.financials}
                />
              ) : (
                <CircularProgress className={classes.centerSpiner} />
              )}
            </AccordionDetails>
          </Accordion>
        )}

        {!pastEvent && menuSections.eventInfo.hasOverallComments && (
          <Accordion
            className={classes.margin}
            onChange={() => {
              handleChange("comment");
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography className={classes.heading}>
                Overall Comments
              </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.padding}>
              {loaded && (
                <CommentBox
                  overalComment={overalComment}
                  canViewComments={canViewComments}
                  notes={menuSections.eventInfo.overallComments.notes.notes}
                  comments={
                    menuSections.eventInfo.overallComments.generalComments
                  }
                  type={menuSections.eventInfo.newComment}
                  setOveralComment={setOveralComment}
                />
              )}
            </AccordionDetails>
          </Accordion>
        )}
      </Grid>
    </Grid>
  );
};
CurrentEvent.propTypes = {
  classes: PropTypes.object.isRequired,
  width: PropTypes.string.isRequired,
};
export default compose(withStyles(styles), withWidth())(CurrentEvent);
export { checkToDisplay };
